import { useUpdate, useList } from '@refinedev/core';
import { Loader2 } from 'lucide-react';
import { FormStickyActions, FormSpacer } from '@/components/forms';
import {
  LanguageSwitcher,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import type { Course } from '../types';
import { CourseFormFields, initFormState, type FormState } from './CourseFormFields';
import { buildTeacherOptions, type TeacherRecord } from './select-utils';

interface LanguageRecord {
  code: string; name: string; nativeName: string;
  flag?: string | null; flagEmoji?: string | null;
}

const TRANSLATION_FIELDS = [
  { name: 'title', label: 'Название курса', type: 'input' as const, required: true },
  { name: 'shortDescription', label: 'Краткое описание', type: 'richtext' as const },
  { name: 'description', label: 'Полное описание', type: 'richtext' as const },
];

interface CourseEditTabProps {
  course: Course;
  onUpdated: () => void;
}

export function CourseEditTab({ course, onUpdated }: CourseEditTabProps) {
  const { mutateAsync: update, mutation } = useUpdate();

  const { result: langResult, query: langQ } = useList<LanguageRecord>({ resource: 'languages' });
  const languages: TranslationLanguage[] = (langResult?.data ?? []).map((l) => ({
    code: l.code, name: l.name, nativeName: l.nativeName, flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const { result: instrResult, query: instrQ } = useList<TeacherRecord>({
    resource: 'teachers',
    pagination: { pageSize: 100 },
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
  });
  const instrOpts = buildTeacherOptions(instrResult?.data ?? []);

  // Форму нельзя монтировать, пока не загружены вспомогательные данные
  // (languages, teachers): useReducer(reducer, initial) фиксирует initial
  // ТОЛЬКО при первом монтировании. Если смонтировать форму на неполном
  // состоянии, reducer навсегда залипнет на дефолтах initFormState
  // (isPublished: false и т.д.), и при submit buildUpdateFormData отправит
  // эти дефолты на бэкенд — курс из «опубликован» станет «черновиком».
  const auxLoading = instrQ.isLoading || langQ.isLoading;
  const loading = mutation.isPending;

  if (auxLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const initial = initFormState(course);

  return (
    <CourseFormFields
      // key завязан на updatedAt: после refetch() (курс реально обновился
      // на бэкенде) форма перемонтируется со свежим initial, reducer
      // переинициализируется актуальными данными вместо устаревшего снимка.
      key={`edit-${course.id}-${course.updatedAt}`}
      isEdit
      loading={loading}
      initial={initial}
      instrOpts={instrOpts}
      languages={languages}
      courseId={String(course.id)}
      renderHeader={(state: FormState, setField) => (
        <div className="flex items-center justify-end gap-3">
          {languages.length > 0 && (
            <LanguageSwitcher
              languages={languages}
              activeLanguage={state.lang}
              onLanguageChange={(c) => setField('lang', c as LanguageCode)}
              fields={TRANSLATION_FIELDS}
              value={state.translations}
              variant="compact"
            />
          )}
        </div>
      )}
      renderFooter={(submit, isLoading) => (
        <>
          <FormSpacer />
          <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <FormStickyActions
              primaryLabel="Сохранить"
              onPrimary={submit}
              isPending={isLoading}
              pendingLabel="Сохранение..."
            />
          </form>
        </>
      )}
      onSubmit={async (data) => {
        await update({
          resource: 'courses',
          id: course.id,
          values: data,
        });
        onUpdated();
      }}
    />
  );
}
